import {
  Box,
  Button,
  TextFieldProps,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import {
  MAXIMUM_ARRAY_LENGTH,
  MAXIMUM_STRING_LENGTH,
  MAXIMUM_TEXT_AREA_LENGTH,
} from '@common/types/constants.ts';

interface IDynamicFormFieldProps {
  name: string;
  label?: string;
  disabled?: boolean;
  optional?: boolean;
  placeholder?: string;
  handleChange: (values: string[]) => void;
  value: string[];
  error?: boolean;
  subtitle?: string;
  title?: string;
  debounceMs?: number;
  multiline?: boolean;
}

type RepeaterTextareaProps = IDynamicFormFieldProps & TextFieldProps;

const ensureAtLeastOne = (arr: string[]) => (arr.length ? arr : ['']);

export const RepeaterTextField = ({
  name,
  label,
  disabled = false,
  optional,
  handleChange,
  value,
  error,
  title,
  subtitle,
  debounceMs = 500,
  multiline,
  ...props
}: RepeaterTextareaProps) => {
  const initial = ensureAtLeastOne(value);

  // Local state mirror for instant typing
  const [localValues, setLocalValues] = useState<string[]>(initial);
  // Track the last snapshot that we actually sent to the parent
  const lastSentJsonRef = useRef(JSON.stringify(initial));

  const debouncedCommit = useMemo(
    () =>
      debounce((vals: string[]) => {
        const json = JSON.stringify(vals);
        if (json === lastSentJsonRef.current) return;
        lastSentJsonRef.current = json;
        handleChange(vals);
      }, debounceMs),
    [handleChange, debounceMs],
  );

  useEffect(() => {
    const localJson = JSON.stringify(localValues);
    const propsJson = JSON.stringify(ensureAtLeastOne(value));
    if (localJson !== propsJson) {
      debouncedCommit(localValues);
    }
    return () => debouncedCommit.cancel();
  }, [localValues, value, debouncedCommit]);

  // Track and sync with changes to the parent state
  useEffect(() => {
    const parentJson = JSON.stringify(ensureAtLeastOne(value));
    if (parentJson === lastSentJsonRef.current) return;

    const localJson = JSON.stringify(localValues);

    if (parentJson !== localJson) setLocalValues(ensureAtLeastOne(value));
  }, [value, localValues]);

  const handleAddField = () =>
    setLocalValues(prev =>
      prev.length < MAXIMUM_ARRAY_LENGTH ? [...prev, ''] : prev,
    );
  const handleDelete = (index: number) =>
    setLocalValues(prev => prev.filter((_, i) => i !== index));

  const tfModeProps: TextFieldProps = multiline
    ? {
        multiline: true,
        minRows: 3,
        maxRows: 3,
        slotProps: { htmlInput: { maxLength: MAXIMUM_TEXT_AREA_LENGTH } },
      }
    : {
        slotProps: { htmlInput: { maxLength: MAXIMUM_STRING_LENGTH } },
      };

  return (
    <Box sx={{ width: '100%', height: 'fit-content' }}>
      <TextField
        id={name}
        name={name}
        sx={{ display: 'none' }}
        value={JSON.stringify(localValues)}
      />

      {title && <Typography variant='subtitle2'>{title}</Typography>}
      {subtitle && (
        <Typography
          variant='caption'
          sx={{ color: 'text.secondary', display: 'block', mt: 0.5, ml: 1 }}
        >
          {subtitle}
        </Typography>
      )}

      <Box
        sx={{
          overflowY: 'scroll',
          height: 'fit-content',
          pt: '0.5rem',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          maxHeight: '10rem',
          '&::-webkit-scrollbar': { width: '10px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            borderRadius: '10px',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&:focus': { outline: 'none' },
          display: 'flex',
          gap: '0.5rem',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {localValues.map((textareaValue, index) => (
          <Box
            key={`${name}-${index}`}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <TextField
                type='text'
                aria-label={`${name} - field number ${index}`}
                label={`Item ${index + 1}`}
                variant='outlined'
                error={!!error}
                required={!optional}
                value={textareaValue}
                onChange={e => {
                  if (disabled) return;
                  const typed = e.target.value;
                  setLocalValues(prev => {
                    const next = [...prev];
                    next[index] = typed;
                    return next;
                  });
                }}
                placeholder={label}
                fullWidth
                disabled={disabled}
                {...tfModeProps}
                {...props}
              />

              <Button
                startIcon={
                  <DeleteIcon
                    sx={{
                      width: '24px',
                      height: '24px',
                    }}
                  />
                }
                sx={{
                  '& .MuiButton-startIcon': { margin: 0 },
                  minWidth: '5px !important',
                }}
                size='small'
                color='error'
                variant='text'
                onClick={() => handleDelete(index)}
                disabled={disabled || localValues.length === 1}
              />
            </Box>
          </Box>
        ))}

        <Button
          sx={{ height: '30px', alignSelf: 'start' }}
          color='primary'
          startIcon={
            <AddIcon
              sx={{ height: '20px' }}
              color={
                localValues.length > 19 || disabled ? 'disabled' : 'primary'
              }
            />
          }
          onClick={handleAddField}
          disabled={localValues.length > 19 || disabled}
        >
          ADD
        </Button>
      </Box>
    </Box>
  );
};
